import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Divider,
  Select,
  Link,
  Circle,
  Skeleton,
  Flex,
} from "@chakra-ui/react";
import Card from "components/card/Card.js";
import useClientPercentageUptime from "hooks/clientDashboard/useClientPercentageUptime";
import useGetMtbf from "hooks/clientDashboard/useGetMtbf";
import useGetMttr from "hooks/clientDashboard/useGetMttr";
import Chart from "react-apexcharts";
import EmptyCard from "components/card/EmptyCard";
import "./clientCharts.css";

const CardView = ({ data, colorCode, tabIndex, toggleCardMode }) => {
  const [perSort, setPerSort] = useState("desc");
  const [mttrSort, setMttrSort] = useState("desc");
  const [mtbfSort, setMtbfSort] = useState("desc");
  const {
    isLoading,
    data: apiData,
    refetch,
    isFetching,
  } = useClientPercentageUptime(perSort);
  const {
    isLoading: isMtbfLoading,
    data: mtbfData,
    refetch: mtbfRefetch,
    isFetching: isMtbfFetching,
  } = useGetMtbf(mtbfSort);
  const {
    isLoading: isMttrLoading,
    data: mttrData,
    refetch: mttrRefetch,
    isFetching: isMttrFetching,
  } = useGetMttr(mttrSort);

  useEffect(() => {
    refetch();
    mtbfRefetch();
    mttrRefetch();
  }, [tabIndex]);

  const handleChange = (e) => {
    setPerSort(e.target.value);
  };

  const handleMttrChange = (e) => {
    setMttrSort(e.target.value);
  };

  const handleMtbfChange = (e) => {
    setMtbfSort(e.target.value);
    mtbfRefetch(e.target.value);
  };

  useEffect(() => {
    refetch();
  }, [perSort]);

  useEffect(() => {
    mttrRefetch();
  }, [mttrSort]);

  useEffect(() => {
    mtbfRefetch();
  }, [mtbfSort]);

  const barChartDataUptime = [
    {
      name: "Uptime",
      data: apiData?.data?.slice(0, 5)?.map((data) => data.uptime_percentage),
    },
  ];

  const barChartDataMttr = [
    {
      name: "mttr",
      data: mttrData?.data?.slice(0, 5)?.map((data) => data.MTTR),
    },
  ];

  const barChartDataMtbf = [
    {
      name: "mtbf",
      data: mtbfData?.data?.slice(0, 5)?.map((data) => data.MTBF),
    },
  ];

  const getSplitString = (string) => {
    var i = string.indexOf(" ");
    var partOne = string.slice(0, i + 10).trim();
    var partTwo = string.slice(i + 6, string.length).trim();
    return partOne;
  };

  const barChartOptionsUptime = {
    chart: {
      stacked: false,
      toolbar: {
        show: false,
      },
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontFamily: undefined,
      },
      onDatasetHover: {
        style: {
          fontSize: "12px",
          fontFamily: undefined,
        },
      },
      theme: "dark",
    },
    xaxis: {
      categories: apiData?.data
        ?.slice(0, 5)
        ?.map((dat) => getSplitString(dat.name)),
      show: true,
      labels: {
        show: true,
        style: {
          colors: "#A3AED0",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      title: {
        text: "Entities",
        style: {
          color: "#9EA2A9",
          fontSize: "13px",
        },
      },
    },
    yaxis: {
      show: true,
      categories: ["0", "10", "20", "30", "40", "50"],
      color: "black",
      labels: {
        show: true,
        style: {
          colors: "#A3AED0",
          fontSize: "14px",
          fontWeight: "500",
        },
      },
      title: {
        text: "Percentage Uptime",
        style: {
          color: "#9EA2A9",
          fontSize: "13px",
        },
      },
    },

    grid: {
      borderColor: "rgba(163, 174, 208, 0.3)",
      show: true,
      yaxis: {
        lines: {
          show: false,
          opacity: 0.5,
        },
      },
      row: {
        opacity: 0.5,
      },
      xaxis: {
        lines: {
          show: false,
        },
      },
    },
    fill: {
      type: "solid",
      colors: [colorCode[0]],
    },
    legend: {
      show: true,
    },
    colors: [colorCode],
    dataLabels: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        borderRadius: 10,
        columnWidth: "30px",
      },
    },
  };

  const barChartOptionsMtbf = {
    chart: {
      stacked: false,
      toolbar: {
        show: false,
      },
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontFamily: undefined,
      },
      onDatasetHover: {
        style: {
          fontSize: "12px",
          fontFamily: undefined,
        },
      },
      theme: "dark",
    },
    xaxis: {
      categories: mtbfData?.data
        ?.slice(0, 5)
        ?.map((dat) => getSplitString(dat.name)),
      show: true,
      labels: {
        show: true,
        style: {
          colors: "#A3AED0",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      title: {
        text: "Entities",
        style: {
          color: "#9EA2A9",
          fontSize: "13px",
        },
      },
    },
    yaxis: {
      show: true,
      categories: ["0", "10", "20", "30", "40", "50"],
      color: "black",
      labels: {
        show: true,
        style: {
          colors: "#A3AED0",
          fontSize: "14px",
          fontWeight: "500",
        },
      },
      title: {
        text: "Hours",
        style: {
          color: "#9EA2A9",
          fontSize: "13px",
        },
      },
    },

    grid: {
      borderColor: "rgba(163, 174, 208, 0.3)",
      show: true,
      yaxis: {
        lines: {
          show: false,
          opacity: 0.5,
        },
      },
      row: {
        opacity: 0.5,
      },
      xaxis: {
        lines: {
          show: false,
        },
      },
    },
    fill: {
      type: "solid",
      colors: [colorCode[1]],
    },
    legend: {
      show: true,
    },
    colors: [colorCode],
    dataLabels: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        borderRadius: 10,
        columnWidth: "30px",
      },
    },
  };

  const barChartOptionsMttr = {
    chart: {
      stacked: false,
      toolbar: {
        show: false,
      },
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontFamily: undefined,
      },
      onDatasetHover: {
        style: {
          fontSize: "12px",
          fontFamily: undefined,
        },
      },
      theme: "dark",
    },
    xaxis: {
      categories: mttrData?.data
        ?.slice(0, 5)
        ?.map((dat) => getSplitString(dat.name)),
      show: true,
      labels: {
        show: true,
        style: {
          colors: "#A3AED0",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      title: {
        text: "Entities",
        style: {
          color: "#9EA2A9",
          fontSize: "13px",
        },
      },
    },
    yaxis: {
      show: true,
      categories: ["0", "10", "20", "30", "40", "50"],
      color: "black",
      labels: {
        show: true,
        style: {
          colors: "#A3AED0",
          fontSize: "14px",
          fontWeight: "500",
        },
      },
      title: {
        text: "Hours",
        style: {
          color: "#9EA2A9",
          fontSize: "13px",
        },
      },
    },

    grid: {
      borderColor: "rgba(163, 174, 208, 0.3)",
      show: true,
      yaxis: {
        lines: {
          show: false,
          opacity: 0.5,
        },
      },
      row: {
        opacity: 0.5,
      },
      xaxis: {
        lines: {
          show: false,
        },
      },
    },
    fill: {
      type: "solid",
      colors: [colorCode[2]],
    },
    legend: {
      show: true,
    },
    colors: [colorCode],
    dataLabels: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        borderRadius: 10,
        columnWidth: "30px",
      },
    },
  };

  return (
    <Box mt="10px">
      <Flex width="100%" flexWrap="wrap">
        {isLoading || isFetching ? (
          <Card mt="20px" mr="10px" minHeight="380px" flexBasis="48%">
            <Skeleton minHeight="360px" flexBasis="40%" />
          </Card>
        ) : (
          <Card
            mt="20px"
            mr="10px"
            height="max-content"
            minHeight="500px"
            //width="100%"
            flexBasis="48%"
            //minWidth="525px"
            //maxWidth="50%"
          >
            <Flex alignItems="center" justifyContent="space-between">
              <Text fontSize="18px" fontWeight="bold" color="#2B3674">
                Percentage Uptime
              </Text>
              <Select
                bg="white"
                fontSize="15px"
                //mt="20px"
                width="200px"
                height="40px"
                id="user_type"
                size="lg"
                ml="10px"
                color="#2C3774"
                fontWeight="bold"
                value={perSort}
                onChange={handleChange}
              >
                <option value="desc">Top 5</option>
                <option value="asc">Bottom 5</option>
              </Select>
            </Flex>
            {apiData?.data?.length > 0 ? (
              <>
                {toggleCardMode ? (
                  <>
                    {apiData?.data?.slice(0, 5).map((data, index) => (
                      <Box key={data?.id}>
                        <Flex
                          mt="20px"
                          pt="10px"
                          justifyContent="space-between"
                        >
                          <Flex direction="column">
                            <Link
                              href={`/admin/entity-dashboard/${data?.id}/${data?.name}`}
                            >
                              <Flex alignItems="center">
                                <Text
                                  color={"ndmsColors.blue.100"}
                                  fontWeight="500"
                                  fontSize="14px"
                                  pr="10px"
                                >
                                  {data?.name}
                                </Text>
                                <Circle
                                  size="20px"
                                  bg="ndmsColors.gray.1300"
                                  color="ndmsColors.blue.100"
                                >
                                  {">"}
                                </Circle>
                              </Flex>
                            </Link>
                            <Text
                              fontWeight="500"
                              color="#7B818A"
                              fontSize="12px"
                            >
                              {data?.Address?.city}
                            </Text>
                          </Flex>
                          <Text
                            fontWeight="bold"
                            color={colorCode[0]}
                            fontSize="16px"
                          >
                            {data?.uptime_percentage}%
                          </Text>
                        </Flex>
                        {index !== 4 ? <Divider pt="10px" /> : null}
                      </Box>
                    ))}
                  </>
                ) : (
                  <Box mt="40px">
                    <Chart
                      options={barChartOptionsUptime}
                      series={barChartDataUptime}
                      type="bar"
                      height="350px"
                      width="100%"
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box mt="30%">
                <EmptyCard />
              </Box>
            )}
          </Card>
        )}
        {isMtbfLoading || isMtbfFetching ? (
          <Card mt="20px" ml="10px" minHeight="380px" flexBasis="48%">
            <Skeleton minHeight="360px" flexBasis="40%" />
          </Card>
        ) : (
          <Card
            mt="20px"
            ml="20px"
            //mr="20px"
            height="max-content"
            minHeight="500px"
            //width="100%"
            flexBasis="48%"
            //minWidth="525px"
            //maxWidth="47%"
          >
            <Flex alignItems="center" justifyContent="space-between">
              <Text fontSize="18px" fontWeight="bold" color="#2B3674">
                Mean Time Between Failure (MTBF)
              </Text>
              <Select
                bg="white"
                fontSize="15px"
                //mt="20px"
                width="200px"
                height="40px"
                id="user_type"
                size="lg"
                ml="10px"
                color="#2C3774"
                fontWeight="bold"
                value={mtbfSort}
                onChange={handleMtbfChange}
              >
                <option value="desc">Top 5</option>
                <option value="asc">Bottom 5</option>
              </Select>
            </Flex>
            {mtbfData?.data?.length > 0 ? (
              <>
                {toggleCardMode ? (
                  <>
                    {mtbfData?.data?.slice(0, 5).map((data, index) => (
                      <Box key={data?.id}>
                        <Flex
                          mt="20px"
                          pt="10px"
                          justifyContent="space-between"
                        >
                          <Flex direction="column">
                            <Link
                              href={`/admin/entity-dashboard/${data?.id}/${data?.name}`}
                            >
                              <Flex alignItems="center">
                                <Text
                                  color={"ndmsColors.blue.100"}
                                  fontWeight="500"
                                  fontSize="14px"
                                  pr="10px"
                                >
                                  {data?.name}
                                </Text>
                                <Circle
                                  size="20px"
                                  bg="ndmsColors.gray.1300"
                                  color="ndmsColors.blue.100"
                                >
                                  {">"}
                                </Circle>
                              </Flex>
                            </Link>
                            <Text
                              fontWeight="500"
                              color="#7B818A"
                              fontSize="12px"
                            >
                              {data?.Address?.city}
                            </Text>
                          </Flex>
                          <Text
                            fontWeight="bold"
                            color={colorCode[1]}
                            fontSize="16px"
                          >
                            {data?.MTBF} hrs
                          </Text>
                        </Flex>
                        {index !== 4 ? <Divider pt="10px" /> : null}
                      </Box>
                    ))}
                  </>
                ) : (
                  <Box mt="40px">
                    <Chart
                      options={barChartOptionsMtbf}
                      series={barChartDataMtbf}
                      type="bar"
                      height="350px"
                      width="100%"
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box mt="30%">
                <EmptyCard />
              </Box>
            )}
          </Card>
        )}
        {isMttrLoading || isMttrFetching ? (
          <Card mt="20px" mr="10px" minHeight="380px" flexBasis="48%">
            <Skeleton minHeight="360px" flexBasis="40%" />
          </Card>
        ) : (
          <Card
            mt="20px"
            mr="20px"
            height="max-content"
            minHeight="500px"
            //width="100%"
            flexBasis="48%"
            //maxWidth="50%"
          >
            <Flex alignItems="center" justifyContent="space-between">
              <Text fontSize="18px" fontWeight="bold" color="#2B3674">
                Mean Time To Repair (MTTR)
              </Text>
              <Select
                bg="white"
                fontSize="15px"
                //mt="20px"
                width="200px"
                height="40px"
                id="user_type"
                size="lg"
                ml="10px"
                color="#2C3774"
                fontWeight="bold"
                value={mttrSort}
                onChange={handleMttrChange}
              >
                <option value="desc">Top 5</option>
                <option value="asc">Bottom 5</option>
              </Select>
            </Flex>
            {mttrData?.data?.length > 0 ? (
              <>
                {toggleCardMode ? (
                  <>
                    {mttrData?.data?.slice(0, 5).map((data, index) => (
                      <Box key={data?.id}>
                        <Flex
                          mt="20px"
                          pt="10px"
                          justifyContent="space-between"
                        >
                          <Flex direction="column">
                            <Link
                              href={`/admin/entity-dashboard/${data?.id}/${data?.name}`}
                            >
                              <Flex alignItems="center">
                                <Text
                                  color={"ndmsColors.blue.100"}
                                  fontWeight="500"
                                  fontSize="14px"
                                  pr="10px"
                                >
                                  {data?.name}
                                </Text>
                                <Circle
                                  size="20px"
                                  bg="ndmsColors.gray.1300"
                                  color="ndmsColors.blue.100"
                                >
                                  {">"}
                                </Circle>
                              </Flex>
                            </Link>
                            <Text
                              fontWeight="500"
                              color="#7B818A"
                              fontSize="12px"
                            >
                              {data?.Address?.city}
                            </Text>
                          </Flex>
                          <Text
                            fontWeight="bold"
                            color={colorCode[1]}
                            fontSize="16px"
                          >
                            {data?.MTTR} hrs
                          </Text>
                        </Flex>
                        {index !== 4 ? <Divider pt="10px" /> : null}
                      </Box>
                    ))}
                  </>
                ) : (
                  <Box mt="40px">
                    <Chart
                      options={barChartOptionsMttr}
                      series={barChartDataMttr}
                      type="bar"
                      height="350px"
                      width="100%"
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box mt="30%">
                <EmptyCard />
              </Box>
            )}
          </Card>
        )}
      </Flex>
    </Box>
  );
};

export default CardView;
